import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronRight, ChevronLeft, Check, Flame, Loader2, Lock, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export default function ForgeStories() {
  const navigate = useNavigate();
  const { languageCode } = useParams();
  const [searchParams] = useSearchParams();
  const course = parseInt(searchParams.get('course') || '1');
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;

  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [activeCourse, setActiveCourse] = useState(course);

  useEffect(() => {
    fetchEpisodes();
  }, [languageCode, activeCourse]);

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/forge-stories/episodes/${languageCode}?course=${activeCourse}`);
      const eps = resp.data.episodes || [];
      // If no episodes for this course, try finding the first course that has episodes
      if (eps.length === 0 && activeCourse === course) {
        for (let c = 1; c <= 4; c++) {
          if (c === activeCourse) continue;
          const fallback = await api.get(`/forge-stories/episodes/${languageCode}?course=${c}`);
          if ((fallback.data.episodes || []).length > 0) {
            setActiveCourse(c);
            return; // Will re-trigger useEffect
          }
        }
      }
      setEpisodes(eps);
      setTotal(resp.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch episodes:', err);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const completedCount = episodes.filter(e => e.completed).length;
  const progressPct = total > 0 ? (completedCount / total) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="forge-stories">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center shadow-lg">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Forge Stories</h1>
            <p className="text-xs text-muted-foreground">Course {activeCourse} — {total} episodes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30"
              data-testid="admin-review-badge"
              title="Admin mode — all episodes unlocked for review"
            >
              <Shield className="h-3 w-3" /> Admin review
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/forge/${languageCode}?course=${course}`)}
            className="gap-1"
            data-testid="go-to-forge-btn"
          >
            <Flame className="h-4 w-4" /> Forge Path
          </Button>
        </div>
      </div>

      {/* Course switcher — lets admins (and users) jump between C1-C4 */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="course-switcher">
        {[1, 2, 3, 4].map((c) => (
          <Button
            key={c}
            size="sm"
            variant={activeCourse === c ? 'default' : 'outline'}
            onClick={() => setActiveCourse(c)}
            className="text-xs h-7"
            data-testid={`course-btn-${c}`}
          >
            Course {c}
          </Button>
        ))}
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{completedCount} / {total} episodes read</span>
          <span className="font-medium">{Math.round(progressPct)}%</span>
        </div>
        <Progress value={progressPct} className="h-2" />
      </div>

      {/* Episode List */}
      <div className="space-y-2" data-testid="episode-list">
        {episodes.map((ep, idx) => {
          const isLocked = !isAdmin && idx > 0 && !episodes[idx - 1]?.completed && !ep.completed;
          return (
            <Card
              key={ep.episode}
              className={`transition-all duration-200 cursor-pointer border ${
                ep.completed 
                  ? 'border-emerald-500/30 bg-emerald-950/10' 
                  : isLocked
                  ? 'opacity-50 border-slate-700/30'
                  : 'border-slate-700/30 hover:border-indigo-500/40 hover:bg-indigo-950/10'
              }`}
              onClick={() => {
                if (!isLocked) {
                  navigate(`/forge-stories/${languageCode}/read/${ep.episode}`);
                }
              }}
              data-testid={`episode-card-${ep.episode}`}
            >
              <CardContent className="p-3 flex items-center gap-3">
                {/* Episode number */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  ep.completed 
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : isLocked
                    ? 'bg-slate-800 text-slate-500'
                    : 'bg-indigo-500/20 text-indigo-400'
                }`}>
                  {ep.completed ? <Check className="h-5 w-5" /> : isLocked ? <Lock className="h-4 w-4" /> : ep.episode}
                </div>

                {/* Episode info */}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm truncate ${isLocked ? 'text-slate-500' : ''}`}>
                    {ep.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Ep. {ep.episode} — {ep.theme} — {ep.word_count} words
                  </p>
                </div>

                {/* Arrow */}
                {!isLocked && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
